/**
 * Каталог музыки: папки на сервере и треки для пользователя; админские операции.
 */
import { CapacitorHttp } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { isPlatform } from '@ionic/vue';
import axios from 'axios';
import type { GoogleMedia } from '@/model/googlemedia.model';

const baseUrl = () => process.env.VUE_APP_API_URL || '';

const getToken = async (): Promise<string> => {
  const { value } = await Preferences.get({ key: 'jwt' });
  return value || '';
};

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getToken();
  const url = `${baseUrl()}api${path}`;
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (isPlatform('ios') || isPlatform('android')) {
    const opts: Record<string, unknown> = { url, method, headers, readTimeout: 60000 };
    if (body != null) {
      headers['Content-Type'] = 'application/json';
      opts.data = JSON.stringify(body);
    }
    const res = await CapacitorHttp.request(opts as any);
    if (res.status >= 400) {
      throw new Error((res.data as { message?: string })?.message || `HTTP ${res.status}`);
    }
    return (res.data as T) ?? (undefined as T);
  }
  const config: Record<string, unknown> = { url: `api${path}`, method, headers };
  if (body != null) config.data = body;
  const { data } = await axios.request<T>(config as any);
  return data as T;
}

export type LibraryMusicAccessMode = 'PUBLIC' | 'RESTRICTED';

export interface ILibraryMusicFolder {
  id: number;
  slug: string;
  title: string;
  sortOrder: number;
  accessMode: LibraryMusicAccessMode;
  allowedUserIds: number[];
}

export const libraryMusicService = {
  async listUserFolders(): Promise<ILibraryMusicFolder[]> {
    return api<ILibraryMusicFolder[]>('GET', '/library-music/folders');
  },

  async listUserTracks(folderId: number): Promise<GoogleMedia[]> {
    return api<GoogleMedia[]>('GET', `/library-music/folders/${folderId}/tracks`);
  },

  async adminListFolders(): Promise<ILibraryMusicFolder[]> {
    return api<ILibraryMusicFolder[]>('GET', '/admin/library-music/folders');
  },

  async adminListTracks(folderId: number): Promise<GoogleMedia[]> {
    return api<GoogleMedia[]>('GET', `/admin/library-music/folders/${folderId}/tracks`);
  },

  async adminCreateFolder(body: Partial<ILibraryMusicFolder>): Promise<ILibraryMusicFolder> {
    return api<ILibraryMusicFolder>('POST', '/admin/library-music/folders', body);
  },

  async adminUpdateFolder(id: number, body: Partial<ILibraryMusicFolder>): Promise<ILibraryMusicFolder> {
    return api<ILibraryMusicFolder>('PUT', `/admin/library-music/folders/${id}`, body);
  },

  async adminDeleteFolder(id: number): Promise<void> {
    await api<unknown>('DELETE', `/admin/library-music/folders/${id}`);
  },
};
